import {useState} from "react";
import {Metadata} from "../models/remoteConstants.model";
import {MetadataCollection} from "../commands/metadataValue.command";
import {useApp} from "../app.context";
import StringInput from "./stringInput.element";
import List from "./list";
import MetadataElement from "./metadata.element";

interface MetadataColumnProps {
    title: string;
    attributes: Metadata[];
    collection: MetadataCollection;
    add(value: string): void;
    remove(value: string): void;
    // When false, hide the "add new entry" UI at the bottom (dropdown/free-text
    // input + Create New button). Used by Tech Passives, which are 1:1 with
    // TechPassive C# classes — adding entries from the editor doesn't make
    // sense because the type set is defined by code, not data.
    allowAdd?: boolean;
}

// Shared column used by the Tech, Attributes, and Rewards tabs. Renders a
// scrollable list of MetadataElements plus an optional "Create New" input.
function MetadataColumn(props: MetadataColumnProps) {
    const {getLocalizationValue, getAttributeManifestEntries} = useApp();
    const [attribute, setAttribute] = useState<string>('');

    const allowAdd = props.allowAdd ?? true;
    const trimmed = attribute.trim();
    const canCreate = trimmed !== '';

    // Every type the manifest knows for this collection, as the data id a new
    // instance should carry (the human alias — last id — else the type name).
    // Unlike the old "available" list this is NOT filtered to unauthored types,
    // so duplicates are allowed (e.g. two Gold rewards with different amounts on
    // different building levels). Empty for non-manifest collections (Tech).
    //
    // Hidden entries (e.g. unit type markers like Ranged/Melee) are excluded —
    // they live in their own column (Unit Types) and are auto-seeded, never
    // created from the dropdown.
    const typeOptions = getAttributeManifestEntries(props.collection)
        .filter(e => !e.Hidden)
        .map(e => e.Ids[e.Ids.length - 1] ?? e.TypeName)
        .sort((a, b) => a.localeCompare(b));
    const isManifestBacked = typeOptions.length > 0;

    return (
        <div className={'layout vertical gap'} style={{width: '30%', flexShrink: 0}}>
            <List title={props.title} elements={props.attributes}
                  headerClassName={'box'}
                  component={element => <MetadataElement metadata={element} collection={props.collection} onDelete={() => props.remove(element.uid)}/>}
                  search={element => getLocalizationValue(element.uid)}
                  keyFor={element => element.uid}
            />
            {allowAdd && (isManifestBacked ? (
                // Manifest-backed collection: a single "Create New" dropdown.
                // Picking a type creates it; the placeholder stays selected
                // (value=''), so the control self-resets for the next create.
                <select style={{width: '100%'}} value={''}
                        onChange={(e) => { if (e.target.value) props.add(e.target.value); }}>
                    <option value={''} disabled hidden>Create New…</option>
                    {typeOptions.map((id) => <option key={id} value={id}>{id}</option>)}
                </select>
            ) : (
                // Non-manifest collection (e.g. Tech): free-text id entry.
                <div className={'layout vertical stretch gap'}>
                    <StringInput value={attribute} placeholder={'Enter attribute name...'} onChange={setAttribute}/>
                    <p style={{
                        color: '#ff6666', visibility: 'hidden',
                        border: 'none', background: 'none', padding: 0, margin: 0
                    }}>
                        &nbsp;
                    </p>
                    <button disabled={!canCreate}
                            style={{opacity: canCreate ? 1 : 0.5, pointerEvents: canCreate ? 'auto' : 'none'}}
                            onClick={() => {
                                props.add(trimmed);
                                setAttribute('');
                            }}>
                        Create New
                    </button>
                </div>
            ))}
        </div>
    );
}

export default MetadataColumn;
